import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useUserManagement } from '@/hooks/useUserManagement';
import { useAuth } from '@/hooks/useAuth';
import DualListBox from '@/components/user-management/DualListBox';
import UserListTable from '@/components/user-management/UserListTable';

interface UserManagementPageProps {
  onComplete?: () => void;
}

/**
 * ユーザー管理ページ
 * 管理者のみアクセス可能
 */
const UserManagementPage: React.FC<UserManagementPageProps> = ({ onComplete }) => {
  const {
    allUsers,
    groups,
    selectedGroup,
    groupMembers,
    nonGroupMembers,
    isLoadingUsers,
    isLoadingGroups,
    isLoadingMembers,
    isUpdating,
    error,
    selectedNonMembers,
    selectedMembers,
    setSelectedGroup,
    setSelectedNonMembers,
    setSelectedMembers,
    addSelectedToGroup,
    removeSelectedFromGroup,
    createNewUser,
    disableExistingUser,
    enableExistingUser,
    refresh,
  } = useUserManagement();

  const { userInfo } = useAuth();

  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // グループに追加
  const handleAddToGroup = async () => {
    const count = selectedNonMembers.length;
    const success = await addSelectedToGroup();
    if (success) {
      setSnackbar({ open: true, message: `${count}名をグループに追加しました`, severity: 'success' });
    } else {
      setSnackbar({ open: true, message: '追加に失敗しました', severity: 'error' });
    }
  };

  // グループから削除
  const handleRemoveFromGroup = async () => {
    const count = selectedMembers.length;
    const success = await removeSelectedFromGroup();
    if (success) {
      setSnackbar({ open: true, message: `${count}名をグループから削除しました`, severity: 'success' });
    } else {
      setSnackbar({ open: true, message: '削除に失敗しました', severity: 'error' });
    }
  };

  // ユーザー無効化
  const handleDisableUser = async (username: string) => {
    const success = await disableExistingUser(username);
    if (success) {
      setSnackbar({ open: true, message: 'ユーザーを無効化しました', severity: 'success' });
    } else {
      setSnackbar({ open: true, message: 'ユーザーの無効化に失敗しました', severity: 'error' });
    }
  };

  // ユーザー有効化
  const handleEnableUser = async (username: string) => {
    const success = await enableExistingUser(username);
    if (success) {
      setSnackbar({ open: true, message: 'ユーザーを有効化しました', severity: 'success' });
    } else {
      setSnackbar({ open: true, message: 'ユーザーの有効化に失敗しました', severity: 'error' });
    }
  };

  const isLoading = isLoadingUsers || isLoadingGroups || isLoadingMembers;

  const leftItems = nonGroupMembers.map(user => ({
    id: user.username,
    label: user.email,
    secondary: user.enabled ? undefined : '(無効)',
  }));

  // 選択中グループの日本語表示名（見つからなければグループ名をそのまま使用）
  const selectedGroupLabel =
    groups.find(g => g.value === selectedGroup)?.label || selectedGroup;

  // administrators グループでは自分自身を右リストから除外（自己操作禁止）
  const isSelfProtectedGroup = selectedGroup === 'administrators';
  const filteredGroupMembers = isSelfProtectedGroup
    ? groupMembers.filter(user => user.email !== userInfo?.email)
    : groupMembers;

  const rightItems = filteredGroupMembers.map(user => ({
    id: user.username,
    label: user.email,
    secondary: user.enabled ? undefined : '(無効)',
  }));

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        {/* ヘッダー */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" fontWeight="bold">
            ユーザー管理
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={onComplete}
              disabled={isLoading}
            >
              ファイル管理に戻る
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={refresh}
              disabled={isLoading}
            >
              更新
            </Button>
          </Box>
        </Box>

        {/* エラー表示 */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* タブ */}
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tab label="ユーザー管理" />
          <Tab label="グループ管理" />
        </Tabs>

        {/* ユーザー管理タブ */}
        {activeTab === 0 && (
          isLoadingUsers ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <UserListTable
              users={allUsers}
              isLoading={isUpdating}
              onDisableUser={handleDisableUser}
              onEnableUser={handleEnableUser}
              onCreateUser={createNewUser}
              onRefresh={refresh}
            />
          )
        )}

        {/* グループ管理タブ */}
        {activeTab === 1 && (
          <>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>グループを選択</InputLabel>
              <Select
                value={selectedGroup || ''}
                label="グループを選択"
                onChange={(e) => setSelectedGroup(e.target.value || null)}
                disabled={isLoadingGroups}
              >
                <MenuItem value="">
                  <em>グループを選択してください</em>
                </MenuItem>
                {groups.map((group) => (
                  <MenuItem key={group.value} value={group.value}>
                    {group.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider sx={{ mb: 3 }} />

            {selectedGroup ? (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Ctrl+クリック: 複数選択 ／ Shift+クリック: 範囲選択
                  </Typography>
                </Box>

                {isLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <DualListBox
                    leftTitle="グループ外のユーザー"
                    rightTitle={`${selectedGroupLabel} のメンバー`}
                    leftItems={leftItems}
                    rightItems={rightItems}
                    selectedLeft={selectedNonMembers}
                    selectedRight={selectedMembers}
                    onSelectLeft={setSelectedNonMembers}
                    onSelectRight={setSelectedMembers}
                    onMoveToRight={handleAddToGroup}
                    onMoveToLeft={handleRemoveFromGroup}
                    isLoading={isUpdating}
                    disabled={isUpdating}
                  />
                )}

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    矢印ボタンをクリックすると即座に反映されます
                  </Typography>
                </Box>
              </>
            ) : (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="body1" color="text.secondary">
                  グループを選択してください
                </Typography>
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* 通知 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagementPage;
